import React, { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import ErrorBoundary from 'components/error_boundary';
import { Button } from 'components/ui/button';

const SectionRetryContext = createContext<(() => void) | null>(null);

// Retry remounts the section: a crashed render gets a fresh tree, and a section whose load failed
// runs its mount-time fetch again.
export function SectionBoundary({ children }: { children: ReactNode }) {
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt(current => current + 1), []);

  return (
    <SectionRetryContext.Provider value={retry}>
      <ErrorBoundary fallback={<div className="p-4"><SectionError /></div>} key={attempt}>{children}</ErrorBoundary>
    </SectionRetryContext.Provider>
  );
}

interface SectionErrorProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
}

export function SectionError({
  title = 'Something went wrong',
  description = 'Try again, or contact support if it keeps happening.',
  onRetry,
}: SectionErrorProps) {
  const remount = useContext(SectionRetryContext);
  const retry = onRetry ?? remount;

  return (
    <Empty className="bg-white">
      <Empty.Media>
        <FontAwesomeIcon icon={faTriangleExclamation} />
      </Empty.Media>
      <Empty.Title>{title}</Empty.Title>
      <Empty.Content>{description}</Empty.Content>
      {retry && (
        <Empty.Actions>
          <Button variant="secondary" onClick={retry}>Retry</Button>
        </Empty.Actions>
      )}
    </Empty>
  );
}

interface SectionEmptyProps {
  icon: IconDefinition;
  title: string;
  description?: string;
}

export function SectionEmpty({ icon, title, description }: SectionEmptyProps) {
  return (
    <Empty className="bg-white">
      <Empty.Media>
        <FontAwesomeIcon icon={icon} />
      </Empty.Media>
      <Empty.Title>{title}</Empty.Title>
      {description && <Empty.Content>{description}</Empty.Content>}
    </Empty>
  );
}
